import {
  Chart as ChartJS,
  CategoryScale,
  LinearScale,
  BarElement,
  LineElement,
  PointElement,
  ArcElement,
  Title,
  Tooltip,
  Legend,
  Filler,
  BarController,
  LineController,
  DoughnutController,
} from 'chart.js';

// Register Chart.js components
ChartJS.register(
  CategoryScale,
  LinearScale,
  BarElement,
  LineElement,
  PointElement,
  ArcElement,
  Title,
  Tooltip,
  Legend,
  Filler,
  BarController,
  LineController,
  DoughnutController
);

interface AdoptionMetricsData {
  adoption: {
    totalBusinesses: { value: number | string; growth?: number };
    payingCustomers: { value: number | string; growth?: number };
  };
  monetization: {
    mrr: { value: number | string; growth?: number };
    arpu: { value: number | string };
  };
  retention: {
    activeUsersAfter2Weeks: { value: number | string; percentage?: number };
  };
  growth: {
    wowSignups: { value: number | string; growth?: number };
    conversionRate: { value: number | string; percentage?: number };
  };
  usage: {
    totalMessages: { value: number | string };
    weeklyInteractions: { value: number | string };
  };
  impact: {
    customerReach: { value: number | string };
    aiConversationsPerMonth: { value: number | string };
  };
}

// Download a single chart with proper sizing and styling
export async function downloadSingleChart(
  data: AdoptionMetricsData,
  chartType: 'revenue' | 'growth' | 'impact' | 'metrics'
): Promise<void> {
  const canvas = document.createElement('canvas');
  canvas.width = 1200;
  canvas.height = 800;

  const container = document.createElement('div');
  container.style.cssText = 'position: absolute; left: -9999px;';
  container.appendChild(canvas);
  document.body.appendChild(container);

  try {
    let chart: ChartJS | null = null;
    let filename = '';

    switch (chartType) {
      case 'revenue':
        filename = 'svarra-revenue-metrics';
        chart = createRevenueChart(canvas, data);
        break;
      case 'growth':
        filename = 'svarra-user-growth';
        chart = createGrowthChart(canvas, data);
        break;
      case 'impact':
        filename = 'svarra-business-impact';
        chart = createImpactChart(canvas, data);
        break;
      case 'metrics':
        filename = 'svarra-key-metrics';
        chart = createKeyMetricsChart(canvas, data);
        break;
    }

    // Wait for chart to render
    await new Promise(resolve => setTimeout(resolve, 500));

    // Download
    canvas.toBlob((blob) => {
      if (blob) {
        const url = URL.createObjectURL(blob);
        const a = document.createElement('a');
        a.href = url;
        a.download = `${filename}-${new Date().toISOString().split('T')[0]}.png`;
        a.click();
        URL.revokeObjectURL(url);
      }
    });

    // Cleanup chart instance
    if (chart) {
      chart.destroy();
    }
  } finally {
    document.body.removeChild(container);
  }
}

function createRevenueChart(canvas: HTMLCanvasElement, data: AdoptionMetricsData): ChartJS {
  const mrr = parseFloat(String(data.monetization.mrr.value).replace(/[$,]/g, '')) || 0;
  const arpu = parseFloat(String(data.monetization.arpu.value).replace(/[$,]/g, '')) || 0;
  const arr = mrr * 12;

  return new ChartJS(canvas, {
    type: 'bar',
    data: {
      labels: ['MRR', 'ARR', 'ARPU'],
      datasets: [{
        label: 'Revenue ($)',
        data: [mrr, arr, arpu],
        backgroundColor: [
          'rgba(99, 102, 241, 0.8)',
          'rgba(139, 92, 246, 0.8)',
          'rgba(236, 72, 153, 0.8)',
        ],
        borderColor: [
          'rgb(99, 102, 241)',
          'rgb(139, 92, 246)',
          'rgb(236, 72, 153)',
        ],
        borderWidth: 3,
      }],
    },
    options: {
      responsive: true,
      maintainAspectRatio: false,
      animation: false,
      plugins: {
        title: {
          display: true,
          text: 'Svarra AI - Revenue Metrics',
          font: { size: 32, weight: 'bold' },
          padding: { top: 20, bottom: 30 },
        },
        legend: {
          display: false,
        },
      },
      scales: {
        x: {
          ticks: {
            font: { size: 18 },
          },
        },
        y: {
          beginAtZero: true,
          ticks: {
            font: { size: 18 },
            callback: (value) => '$' + value,
          },
        },
      },
    },
  });
}

function createGrowthChart(canvas: HTMLCanvasElement, data: AdoptionMetricsData): ChartJS {
  const totalUsers = parseInt(String(data.adoption.totalBusinesses.value)) || 0;
  const payingUsers = parseInt(String(data.adoption.payingCustomers.value)) || 0;
  const totalGrowth = data.adoption.totalBusinesses.growth || 0;
  const payingGrowth = data.adoption.payingCustomers.growth || 0;

  const weeks = ['Week 1', 'Week 2', 'Week 3', 'Week 4', 'Current'];
  const totalData = generateGrowthData(totalUsers, totalGrowth, 5);
  const payingData = generateGrowthData(payingUsers, payingGrowth, 5);

  return new ChartJS(canvas, {
    type: 'line',
    data: {
      labels: weeks,
      datasets: [
        {
          label: 'Total Users',
          data: totalData,
          borderColor: 'rgb(59, 130, 246)',
          backgroundColor: 'rgba(59, 130, 246, 0.1)',
          fill: true,
          tension: 0.4,
          borderWidth: 3,
          pointRadius: 6,
        },
        {
          label: 'Paying Customers',
          data: payingData,
          borderColor: 'rgb(34, 197, 94)',
          backgroundColor: 'rgba(34, 197, 94, 0.1)',
          fill: true,
          tension: 0.4,
          borderWidth: 3,
          pointRadius: 6,
        },
      ],
    },
    options: {
      responsive: true,
      maintainAspectRatio: false,
      animation: false,
      plugins: {
        title: {
          display: true,
          text: 'Svarra AI - User Growth Trend',
          font: { size: 32, weight: 'bold' },
          padding: { top: 20, bottom: 30 },
        },
        legend: {
          labels: {
            font: { size: 18 },
            padding: 20,
          },
        },
      },
      scales: {
        x: {
          ticks: {
            font: { size: 18 },
          },
        },
        y: {
          beginAtZero: true,
          ticks: {
            font: { size: 18 },
          },
        },
      },
    },
  });
}

function createImpactChart(canvas: HTMLCanvasElement, data: AdoptionMetricsData): ChartJS {
  const customerReach = parseInt(String(data.impact.customerReach.value)) || 0;
  const conversations = parseInt(String(data.impact.aiConversationsPerMonth.value)) || 0;
  const messages = parseInt(String(data.usage.totalMessages.value)) || 0;

  return new ChartJS(canvas, {
    type: 'bar',
    data: {
      labels: ['Customer Reach', 'AI Conversations', 'Total Messages'],
      datasets: [{
        label: 'Business Impact',
        data: [customerReach, conversations, messages],
        backgroundColor: [
          'rgba(59, 130, 246, 0.8)',
          'rgba(34, 197, 94, 0.8)',
          'rgba(168, 85, 247, 0.8)',
        ],
        borderColor: [
          'rgb(59, 130, 246)',
          'rgb(34, 197, 94)',
          'rgb(168, 85, 247)',
        ],
        borderWidth: 3,
      }],
    },
    options: {
      responsive: true,
      maintainAspectRatio: false,
      animation: false,
      plugins: {
        title: {
          display: true,
          text: 'Svarra AI - Business Impact',
          font: { size: 32, weight: 'bold' },
          padding: { top: 20, bottom: 30 },
        },
        legend: {
          display: false,
        },
      },
      scales: {
        x: {
          ticks: {
            font: { size: 18 },
          },
        },
        y: {
          beginAtZero: true,
          ticks: {
            font: { size: 18 },
          },
        },
      },
    },
  });
}

function createKeyMetricsChart(canvas: HTMLCanvasElement, data: AdoptionMetricsData): ChartJS {
  const conversionRate = parseFloat(String(data.growth.conversionRate.percentage)) || 0;
  const retentionRate = parseFloat(String(data.retention.activeUsersAfter2Weeks.percentage)) || 0;
  const wowGrowth = parseFloat(String(data.growth.wowSignups.growth)) || 0;
  const mrrGrowth = parseFloat(String(data.monetization.mrr.growth)) || 0;

  return new ChartJS(canvas, {
    type: 'bar',
    data: {
      labels: ['Conversion Rate', 'Retention Rate', 'WoW Growth', 'MRR Growth'],
      datasets: [{
        label: 'Percentage (%)',
        data: [conversionRate, retentionRate, wowGrowth, mrrGrowth],
        backgroundColor: [
          'rgba(34, 197, 94, 0.8)',
          'rgba(59, 130, 246, 0.8)',
          'rgba(139, 92, 246, 0.8)',
          'rgba(236, 72, 153, 0.8)',
        ],
        borderColor: [
          'rgb(34, 197, 94)',
          'rgb(59, 130, 246)',
          'rgb(139, 92, 246)',
          'rgb(236, 72, 153)',
        ],
        borderWidth: 3,
      }],
    },
    options: {
      responsive: true,
      maintainAspectRatio: false,
      animation: false,
      plugins: {
        title: {
          display: true,
          text: 'Svarra AI - Key Performance Metrics',
          font: { size: 32, weight: 'bold' },
          padding: { top: 20, bottom: 30 },
        },
        legend: {
          display: false,
        },
      },
      scales: {
        x: {
          ticks: {
            font: { size: 18 },
          },
        },
        y: {
          ticks: {
            font: { size: 18 },
            callback: (value) => value + '%',
          },
        },
      },
    },
  });
}

function generateGrowthData(currentValue: number, growthRate: number, periods: number): number[] {
  const data: number[] = [];
  const growthMultiplier = 1 + (growthRate / 100);

  for (let i = periods - 1; i >= 0; i--) {
    const value = Math.round(currentValue / Math.pow(growthMultiplier, i));
    data.push(value);
  }

  return data;
}
